import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["invoiceSelect", "paymentSelect", "invoiceAmounts", "paymentAmounts", "pairs", "paymentPairs", "submit"];

  connect() {
    // Validar formulario al cargar, solo si el submit target existe
    if (this.hasSubmitTarget) {
      this.validateForm();
    }
  }

  openModal() {
    // Limpiar elementos residuales de Slim Select antes de abrir el modal
    const slimSelectDropdowns = document.querySelectorAll('.ss-content[data-id^="ss-"]');
    slimSelectDropdowns.forEach(function(dropdown) {
      try {
        dropdown.remove();
      } catch (e) {
        console.warn('Error removing SlimSelect dropdown:', e);
      }
    });

    // Abrir el modal cuando Turbo carga el contenido (Bootstrap 4 con jQuery)
    const modal = document.getElementById('linkPaymentInvoiceModal');
    if (modal && typeof $ !== 'undefined' && $.fn.modal) {
      // Remover aria-hidden antes de mostrar el modal para evitar warnings de accesibilidad
      modal.removeAttribute('aria-hidden');
      $(modal).modal('show');
    }
    // Validar formulario después de abrir el modal
    if (this.hasSubmitTarget) {
      this.validateForm();
    }
  }

  closeModal() {
    const modal = document.getElementById('linkPaymentInvoiceModal');
    if (!modal) return;

    // Quitar el foco del modal antes de cerrarlo
    const activeElement = document.activeElement;
    if (activeElement && modal.contains(activeElement)) {
      activeElement.blur();
    }
    if (activeElement === modal) {
      modal.blur();
    }

    // Mover el foco al body
    document.body.focus();

    // Cerrar el modal con Bootstrap
    if (typeof $ !== 'undefined' && $.fn.modal) {
      $(modal).modal('hide');
    }
  }

  updateAmountFields() {
    // Vincular pago a facturas
    const select = this.invoiceSelectTarget;
    if (!select) return;

    const selectedOptions = Array.from(select.selectedOptions || []);
    const amountsDiv = this.invoiceAmountsTarget;
    const amountsList = document.getElementById('invoice-amounts-list');

    if (selectedOptions.length > 0) {
      amountsDiv.style.display = "block";
      if (amountsList) amountsList.innerHTML = "";

      selectedOptions.forEach((option) => {
        const invoiceId = option.value;
        if (!invoiceId) return;

        const remainingCents = parseInt(option.getAttribute('data-invoice-remaining')) || 0;
        const remainingPesos = remainingCents / 100.0;
        // paymentValue ahora contiene el remanente disponible del pago (si está parcialmente vinculado) o el total (si no está vinculado)
        const paymentAvailableCents = parseInt(select.dataset.paymentValue || '0');
        const paymentAvailablePesos = paymentAvailableCents / 100.0;

        // El máximo es el menor entre el pendiente de la factura y el remanente disponible del pago
        const maxAmount = Math.min(remainingPesos, paymentAvailablePesos);

        const amountDiv = document.createElement("div");
        amountDiv.className = "form-group";
        amountDiv.innerHTML = `
          <label>Factura #${invoiceId} - Monto máximo a vincular: $${maxAmount.toFixed(2)} - Pendiente: $${remainingPesos.toFixed(2)}</label>
          <input type="number"
                 name="invoice_amounts[${invoiceId}]"
                 class="form-control invoice-amount-input"
                 min="0"
                 max="${maxAmount.toFixed(2)}"
                 step="0.01"
                 value="${maxAmount.toFixed(2)}"
                data-invoice-id="${invoiceId}"
                data-max-amount="${maxAmount.toFixed(2)}"
                data-invoice-remaining="${remainingPesos.toFixed(2)}"
                data-payment-available="${paymentAvailablePesos.toFixed(2)}"
                data-action="input->link-payment-form#updatePairs blur->link-payment-form#formatInputValue input->link-payment-form#showValidationMessage">
          <small class="text-danger validation-message" id="validation-message-${invoiceId}" style="display: none;"></small>
        `;
        if (amountsList) amountsList.appendChild(amountDiv);
      });
    } else {
      amountsDiv.style.display = "none";
      if (amountsList) amountsList.innerHTML = "";
    }

    this.updatePairs();
    this.validateForm();
  }

  updatePaymentAmountFields() {
    // Vincular factura a pagos
    const select = this.paymentSelectTarget;
    if (!select) return;

    const selectedOptions = Array.from(select.selectedOptions || []);
    const amountsDiv = this.paymentAmountsTarget;
    const amountsList = document.getElementById('payment-amounts-list');

    if (selectedOptions.length > 0) {
      amountsDiv.style.display = "block";
      if (amountsList) amountsList.innerHTML = "";

      selectedOptions.forEach((option) => {
        const paymentId = option.value;
        if (!paymentId) return;

        const remainingCents = parseInt(option.getAttribute('data-payment-remaining')) || 0;
        const remainingPesos = remainingCents / 100.0;
        const invoiceRemainingCents = parseInt(select.dataset.invoiceRemaining || '0');
        const invoiceRemainingPesos = invoiceRemainingCents / 100.0;

        // El máximo es el menor entre el disponible del pago y el pendiente de la factura
        const maxAmount = Math.min(remainingPesos, invoiceRemainingPesos);

        const amountDiv = document.createElement("div");
        amountDiv.className = "form-group";
        amountDiv.innerHTML = `
          <label>Pago #${paymentId} - Máximo a vincular: $${maxAmount.toFixed(2)}</label>
          <input type="number"
                 name="payment_amounts[${paymentId}]"
                 class="form-control payment-amount-input"
                 min="0"
                 max="${maxAmount.toFixed(2)}"
                 step="0.01"
                 value="${maxAmount.toFixed(2)}"
                 data-payment-id="${paymentId}"
                 data-max-amount="${maxAmount.toFixed(2)}"
                 data-invoice-remaining="${invoiceRemainingPesos.toFixed(2)}"
                 data-action="input->link-payment-form#updatePaymentPairs blur->link-payment-form#formatInputValue input->link-payment-form#showValidationMessage">
          <small class="text-danger validation-message" id="validation-message-payment-${paymentId}" style="display: none;"></small>
        `;
        if (amountsList) amountsList.appendChild(amountDiv);
      });
    } else {
      amountsDiv.style.display = "none";
      if (amountsList) amountsList.innerHTML = "";
    }

    this.updatePaymentPairs();
    this.validateForm();
  }

  formatInputValue(event) {
    const input = event.target;
    const value = parseFloat(input.value) || 0;
    input.value = value.toFixed(2);
    if (input.classList.contains('invoice-amount-input')) {
      this.updatePairs();
    } else if (input.classList.contains('payment-amount-input')) {
      this.updatePaymentPairs();
    }
    this.validateForm();
  }

  updatePairs() {
    const amountInputs = document.querySelectorAll(".invoice-amount-input");
    const pairs = {};

    amountInputs.forEach((input) => {
      const invoiceId = input.getAttribute('data-invoice-id');
      const value = parseFloat(input.value) || 0;
      if (invoiceId && value > 0) {
        pairs[invoiceId] = Math.round(value * 100); // Convertir a centavos
      }
    });

    if (this.hasPairsTarget) {
      this.pairsTarget.value = JSON.stringify(pairs);
    }
  }

  updatePaymentPairs() {
    const amountInputs = document.querySelectorAll(".payment-amount-input");
    const pairs = {};

    amountInputs.forEach((input) => {
      const paymentId = input.getAttribute('data-payment-id');
      const value = parseFloat(input.value) || 0;
      if (paymentId && value > 0) {
        pairs[paymentId] = Math.round(value * 100); // Convertir a centavos
      }
    });

    if (this.hasPaymentPairsTarget) {
      this.paymentPairsTarget.value = JSON.stringify(pairs);
    }
  }

  showValidationMessage(event) {
    const input = event.target;
    const value = parseFloat(input.value) || 0;
    const max = parseFloat(input.max) || 0;

    let messageElement;
    if (input.classList.contains('invoice-amount-input')) {
      const invoiceId = input.getAttribute('data-invoice-id');
      messageElement = document.getElementById(`validation-message-${invoiceId}`);
      // payment-available ahora contiene el remanente disponible del pago
      const paymentAvailable = parseFloat(input.getAttribute('data-payment-available')) || 0;
      const invoiceRemaining = parseFloat(input.getAttribute('data-invoice-remaining')) || 0;

      if (value > max) {
        // Determinar qué límite se está excediendo
        // max ya es el menor entre invoiceRemaining y paymentAvailable
        if (invoiceRemaining < paymentAvailable) {
          messageElement.textContent = `El monto no puede exceder el pendiente de la factura ($${invoiceRemaining.toFixed(2)})`;
        } else {
          messageElement.textContent = `El monto no puede exceder el remanente disponible del pago ($${paymentAvailable.toFixed(2)})`;
        }
        messageElement.style.display = "block";
      } else {
        messageElement.style.display = "none";
      }
    } else if (input.classList.contains('payment-amount-input')) {
      const paymentId = input.getAttribute('data-payment-id');
      messageElement = document.getElementById(`validation-message-payment-${paymentId}`);
      const invoiceRemaining = parseFloat(input.getAttribute('data-invoice-remaining')) || 0;

      if (value > max) {
        if (max < invoiceRemaining) {
          messageElement.textContent = `El monto no puede exceder el disponible del pago ($${max.toFixed(2)})`;
        } else {
          messageElement.textContent = `El monto no puede exceder el pendiente de la factura ($${max.toFixed(2)})`;
        }
        messageElement.style.display = "block";
      } else {
        messageElement.style.display = "none";
      }
    }

    this.validateForm();
  }

  submitWithLoading(event) {
    event.preventDefault();

    const form = this.element.closest('form') || this.element;

    // Close the modal first
    this.closeModal();

    // Show loading overlay
    this.showLoadingOverlay();

    // Disable submit button to prevent double-click
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
    }

    // Submit the form
    form.requestSubmit();
  }

  showLoadingOverlay() {
    const messages = [
      "Vinculando pago con la factura...",
      "Sincronizando con Colppy...",
      "Actualizando el estado de cobro...",
      "Casi listo, verificando los montos...",
      "Confirmando la receta financiera...",
      "El chef está cerrando la cuenta..."
    ];

    const overlay = document.createElement('div');
    overlay.className = 'invoice-loading-overlay';
    overlay.id = 'link-payment-loading-overlay';
    overlay.innerHTML = `
      <div class="invoice-loading-content">
        <div class="invoice-loading-receipt">
          <div class="receipt-printer">
            <div class="printer-body">
              <div class="printer-slot"></div>
            </div>
            <div class="receipt-paper">
              <div class="receipt-line"></div>
              <div class="receipt-line short"></div>
              <div class="receipt-line"></div>
              <div class="receipt-line medium"></div>
              <div class="receipt-line short"></div>
            </div>
          </div>
        </div>
        <p class="invoice-loading-message">${messages[0]}</p>
        <div class="invoice-loading-dots">
          <span></span><span></span><span></span>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    // Rotate messages every 2.5 seconds
    let index = 0;
    this._messageInterval = setInterval(() => {
      index = (index + 1) % messages.length;
      const msgEl = overlay.querySelector('.invoice-loading-message');
      if (msgEl) {
        msgEl.style.opacity = '0';
        setTimeout(() => {
          msgEl.textContent = messages[index];
          msgEl.style.opacity = '1';
        }, 300);
      }
    }, 2500);
  }

  removeLoadingOverlay() {
    const overlay = document.getElementById('link-payment-loading-overlay');
    if (overlay) {
      overlay.remove();
    }
    if (this._messageInterval) {
      clearInterval(this._messageInterval);
      this._messageInterval = null;
    }
  }

  validateForm() {
    if (!this.hasSubmitTarget) return;

    const submitButton = this.submitTarget;
    let isValid = false;

    // Validar si es vinculación de pago a facturas
    if (this.hasInvoiceSelectTarget) {
      const select = this.invoiceSelectTarget;
      const selectedOptions = Array.from(select.selectedOptions || []);
      isValid = selectedOptions.length > 0;

      if (isValid) {
        const amountInputs = document.querySelectorAll(".invoice-amount-input");
        amountInputs.forEach((input) => {
          const value = parseFloat(input.value) || 0;
          const max = parseFloat(input.max) || 0;
          if (value > max || value <= 0) {
            isValid = false;
          }
        });
      }
    }

    // Validar si es vinculación de factura a pagos
    if (this.hasPaymentSelectTarget) {
      const select = this.paymentSelectTarget;
      const selectedOptions = Array.from(select.selectedOptions || []);
      isValid = selectedOptions.length > 0;

      if (isValid) {
        const amountInputs = document.querySelectorAll(".payment-amount-input");
        amountInputs.forEach((input) => {
          const value = parseFloat(input.value) || 0;
          const max = parseFloat(input.max) || 0;
          if (value > max || value <= 0) {
            isValid = false;
          }
        });
      }
    }

    submitButton.disabled = !isValid;
  }
}
